import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemText,
  Typography,
  Box,
  Chip,
  IconButton,
  CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useAuth, Organization } from '../contexts/AuthContext';
import { logger } from '../utils/logger';

interface OrganizationSwitchModalProps {
  open: boolean;
  onClose: () => void;
  organizations: Organization[];
}

const OrganizationSwitchModal: React.FC<OrganizationSwitchModalProps> = ({
  open,
  onClose,
  organizations
}) => {
  const { selectAccount } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [loadingId, setLoadingId] = useState<string | null>(null);

  const handleOrganizationSelect = async (userId: string) => {
    logger.info(`Switching to organization with user ID: ${userId}`);
    setIsLoading(true);
    setLoadingId(userId);
    
    try {
      await selectAccount(userId);
      logger.info('Organization switch completed');
    } catch (error) {
      logger.error('Error switching organization:', error);
      setIsLoading(false);
      setLoadingId(null);
      // Keep modal open if there's an error
      return;
    }
    
    // Only close modal after successful completion or when navigating away
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={!isLoading ? onClose : undefined}
      maxWidth="sm" 
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          Switch Organization
          {!isLoading && (
            <IconButton edge="end" onClick={onClose} aria-label="close">
              <CloseIcon />
            </IconButton>
          )}
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Select an organization to switch to:
        </Typography>
        <List>
          {organizations.map((org) => (
            <ListItem
              key={org.id}
              button
              onClick={() => !isLoading && handleOrganizationSelect(org.id)}
              disabled={org.currentlyActive || isLoading}
              sx={{
                bgcolor: org.currentlyActive ? 'action.selected' : 'transparent',
                '&:hover': {
                  bgcolor: org.currentlyActive ? 'action.selected' : 'action.hover',
                },
                borderRadius: 1,
                mb: 1,
              }}
            >
              <ListItemText
                primary={
                  <Box display="flex" alignItems="center">
                    {org.organizationName || 'Organization'}
                    {org.currentlyActive && (
                      <Chip
                        label="Current"
                        size="small"
                        color="primary"
                        sx={{ ml: 1 }}
                      />
                    )}
                    {loadingId === org.id && (
                      <CircularProgress size={16} sx={{ ml: 1 }} />
                    )}
                  </Box>
                }
                secondary={`Role: ${org.role}`}
              />
            </ListItem>
          ))}
        </List>
      </DialogContent>
    </Dialog>
  );
};

export default OrganizationSwitchModal; 